import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getRealtimeConfig,
  listSyncRuns,
  runRealtimeNow,
  type SyncKind,
} from "@/api/sync";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Spinner } from "@/components/Spinner";
import { StatusBadge } from "@/components/StatusBadge";
import {
  fmtDateTime,
  fmtDuration,
  fmtNumber,
  fmtRelative,
} from "@/lib/format";

const KIND_OPTIONS: { value: SyncKind | ""; label: string }[] = [
  { value: "", label: "All kinds" },
  { value: "realtime", label: "Realtime" },
  { value: "backfill", label: "Backfill" },
];

export function SyncRuns() {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<SyncKind | "">("");
  const [notice, setNotice] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ["sync-runs", kind || "all"],
    queryFn: () => listSyncRuns(kind || undefined, 50),
    refetchInterval: (q) => {
      // Refetch fast when something is running.
      const v = q.state.data;
      return v?.some((r) => r.status === "running") ? 2_000 : 10_000;
    },
  });

  const configQuery = useQuery({
    queryKey: ["realtime", "config"],
    queryFn: getRealtimeConfig,
    refetchInterval: 5_000,
  });

  const runNowMutation = useMutation({
    mutationFn: runRealtimeNow,
    onSuccess: (resp) => {
      setNotice(resp.message);
      queryClient.invalidateQueries({ queryKey: ["sync-runs", kind || "all"] });
      queryClient.invalidateQueries({ queryKey: ["realtime", "config"] });
    },
  });

  const anyError = runsQuery.error || configQuery.error || runNowMutation.error;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sync runs"
        subtitle="History of realtime + backfill cycles, and manual realtime trigger"
      />

      {anyError && (
        <ErrorBanner
          error={anyError}
          onDismiss={() => runNowMutation.reset()}
        />
      )}
      {notice && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {/* Realtime control card */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Realtime worker</div>
          {configQuery.data?.active && (
            <span className="badge-running">Manual run in progress</span>
          )}
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Stat
              label="Poll interval"
              value={
                configQuery.data
                  ? `${configQuery.data.poll_interval_seconds}s`
                  : "—"
              }
            />
            <Stat
              label="Lookback"
              value={
                configQuery.data
                  ? `${configQuery.data.lookback_days}d`
                  : "—"
              }
            />
            <Stat
              label="Max pages / run"
              value={
                configQuery.data
                  ? fmtNumber(configQuery.data.max_pages_per_run)
                  : "—"
              }
            />
            <div className="flex items-end justify-end">
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => {
                  setNotice(null);
                  runNowMutation.mutate();
                }}
                disabled={
                  runNowMutation.isPending || configQuery.data?.active
                }
                title={
                  configQuery.data?.active
                    ? "A manual run is already in progress"
                    : "Trigger one realtime cycle now"
                }
              >
                {(runNowMutation.isPending || configQuery.data?.active) && (
                  <Spinner size="sm" />
                )}
                Sync now
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            The scheduled worker container also runs every{" "}
            {configQuery.data?.poll_interval_seconds ?? "?"}s in the
            background — this button is for ad-hoc syncs (e.g. after
            refreshing the cerodeuda cookie).
          </p>
        </div>
      </div>

      {/* History table */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Recent runs</div>
          <select
            className="input max-w-[160px]"
            value={kind}
            onChange={(e) => setKind(e.target.value as SyncKind | "")}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Kind</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Window</th>
                  <th className="text-right">Pages</th>
                  <th className="text-right">New</th>
                  <th className="text-right">Updated</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {runsQuery.isLoading && (
                  <tr>
                    <td colSpan={10} className="p-6 text-center text-slate-500">
                      <Spinner /> Loading…
                    </td>
                  </tr>
                )}
                {runsQuery.data?.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-6 text-center text-slate-500">
                      No runs yet.
                    </td>
                  </tr>
                )}
                {runsQuery.data?.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono text-xs text-slate-500">
                      {r.id}
                    </td>
                    <td>
                      <span className="badge-neutral">{r.kind}</span>
                    </td>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="whitespace-nowrap text-slate-700">
                      <div>{fmtDateTime(r.started_at)}</div>
                      <div className="text-xs text-slate-400">
                        {fmtRelative(r.started_at)}
                      </div>
                    </td>
                    <td className="text-slate-700">
                      {fmtDuration(r.started_at, r.finished_at)}
                    </td>
                    <td className="text-slate-600">
                      {r.date_window_start && r.date_window_end ? (
                        <>
                          {r.date_window_start} → {r.date_window_end}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="text-right tabular-nums">
                      {fmtNumber(r.pages_fetched)}
                    </td>
                    <td className="text-right tabular-nums text-emerald-700">
                      {fmtNumber(r.new_leads)}
                    </td>
                    <td className="text-right tabular-nums text-sky-700">
                      {fmtNumber(r.updated_leads)}
                    </td>
                    <td className="max-w-xs truncate text-rose-700">
                      {r.error_message ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums text-slate-900">
        {value}
      </div>
    </div>
  );
}
